import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AuthorizationService, RequestAccess, RequestUserPermission } from '@myapp/modules/fwcore';
// PbPermissionCodeService
@Injectable({ providedIn: 'root' })
export class PermissionCodeService {

  /** Route used to probe user licensing; any route every licensed user holds. */
  private readonly ctRouteLicenseProbe = '/home';

  /** Cached user-level license result. */
  private license$?: Observable<boolean>;

  /** Codes per route path, cached for the session. */
  private cache = new Map<string, Observable<Set<string>>>();

  constructor(private authService: AuthorizationService) {
  }

  /** Resolves the permission codes held for a route, one request per route. */
  public codesForRoute$(routePath: string): Observable<Set<string>> {
    const key = routePath.toLowerCase();
    let codes$ = this.cache.get(key);

    if (!codes$) {
      const req: RequestUserPermission = { route: key };
      codes$ = this.authService.pagePermissionsGet$(req)
        .pipe(map(resp => new Set((resp.data ?? [])
          .map(p => p.code)
          .filter((c): c is string => !!c)))
          , shareReplay({ bufferSize: 1, refCount: false }));
      this.cache.set(key, codes$);
    }

    return codes$;
  }

  /** Resolves whether the user is licensed, one request per session. */
  public licenseValid$(): Observable<boolean> {
    if (!this.license$) {
      const req = new RequestAccess();
      req.route = this.ctRouteLicenseProbe;
      this.license$ = this.authService.hasAccess$(req)
        .pipe(map(result => result.isLicensed === true)
          , catchError(() => of(false))
          , shareReplay({ bufferSize: 1, refCount: false }));
    }

    return this.license$;
  }

  /** True when the user holds at least one of the supplied codes. */
  public hasAnyCode$(routePath: string, codes: string[]): Observable<boolean> {
    return this.codesForRoute$(routePath)
      .pipe(map(held => codes.some(c => held.has(c))));
  }

  /** Clears cached license/permission state — call after any action that could change it (e.g. self-enroll). */
  public resetCache(): void {
    this.clear();
  }

  /** Drops cached codes and licensing; call on logout or user switch. */
  protected clear(): void {
    this.cache.clear();
    this.license$ = undefined;
  }
}
